import { useState } from "react";
import type React from "react";
import { i18n } from "@frontend/i18n";
import {
  BookUser,
  ChevronDown,
  Files,
  Lock,
  Mail,
  MessagesSquare,
} from "lucide-react";
import { ThemeMode } from "@shared/types/theme-mode.types";
import { UserAppRoutes } from "@shared/constants/user-app-routes.constants";
import { useEventTheme } from "@/shared/presentation/hooks/useEventTheme";

const WHATSAPP_LINK = "https://wa.me/message/TXLWYI333DIMJ1";
const INVITING_HOST = "Riverwood Capital";

const FEATURES = [
  {
    id: "directory-access",
    icon: BookUser,
    textKey: "user.ltf.feature.directory",
  },
  {
    id: "intros",
    icon: MessagesSquare,
    textKey: "user.ltf.feature.intros",
  },
  {
    id: "materials",
    icon: Files,
    textKey: "user.ltf.feature.materials",
  },
] as const;

export function Ltf2026OnboardingPage(): React.JSX.Element {
  useEventTheme(ThemeMode.LIGHT);
  const [isQrExpanded, setIsQrExpanded] = useState(false);

  return (
    <div
      className="flex min-h-screen flex-col items-center gap-6 bg-background px-6 py-6"
      data-testid="ltf-onboarding-page"
    >
      <div className="flex w-full max-w-sm flex-col gap-8">
        {/* Invite banner */}
        <div className="flex items-start gap-3 rounded-md border border-card-foreground/10 bg-card-foreground/10 px-4 py-3">
          <Mail className="mt-0.5 size-4 shrink-0 text-card-foreground" />
          <div className="flex flex-col">
            <p className="truncate text-sm font-medium text-card-foreground">
              {i18n.t("user.ltf.invitedBy", { host: INVITING_HOST })}
            </p>
            <p className="text-sm text-muted-foreground">
              {i18n.t("user.ltf.poweredBy")}
            </p>
          </div>
        </div>

        {/* Hero title */}
        <h1 className="text-5xl capitalize leading-[1.1] font-serif">
          <span className="font-bold text-[hsl(var(--ltf-brand-blue-hsl))]">
            LTF Hub
            <br />
          </span>
          <span className="font-normal text-foreground">
            {i18n.t("user.ltf.hero.suffix")}
          </span>
        </h1>

        {/* Feature list */}
        <div className="flex flex-col gap-4">
          {FEATURES.map((feature) => (
            <div key={feature.id} className="flex items-start gap-1.5">
              <feature.icon className="mt-0.5 size-4 shrink-0 text-foreground" />
              <p className="text-base leading-[1.2] text-foreground">
                {i18n.t(feature.textKey)}
              </p>
            </div>
          ))}
        </div>

        {/* CTA section */}
        <div className="flex flex-col gap-3">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-11 items-center justify-center rounded-full bg-foreground text-base font-semibold text-background shadow-sm"
            data-testid="ltf-whatsapp-cta"
          >
            {i18n.t("user.ltf.cta.openWhatsapp")}
          </a>

          <div
            className={`flex flex-col items-center border border-foreground shadow-sm ${
              isQrExpanded ? "rounded-3xl pb-4" : "rounded-full"
            }`}
          >
            <button
              type="button"
              data-testid="ltf-qr-toggle-button"
              onClick={() => {
                setIsQrExpanded((prev) => !prev);
              }}
              aria-expanded={isQrExpanded}
              aria-controls="ltf-qr-panel"
              className="flex h-11 w-full items-center justify-center gap-2 text-base font-medium text-foreground"
            >
              <span>{i18n.t("user.ltf.qr.toggle")}</span>
              <ChevronDown
                className={`size-5 ${isQrExpanded ? "rotate-180" : ""}`}
              />
            </button>
            {/* Sibling of the toggle, not a child: a tap on the code while
              someone lines up a scan must not collapse it. */}
            {isQrExpanded && (
              <div
                id="ltf-qr-panel"
                className="flex flex-col items-center gap-2 px-4"
              >
                <img
                  src="/assets/ltf2026-qr.png"
                  alt={i18n.t("user.ltf.qr.alt")}
                  className="size-[261px]"
                  data-testid="ltf-qr-image"
                />
                <p className="text-sm font-medium text-muted-foreground">
                  {i18n.t("user.ltf.qr.caption")}
                </p>
              </div>
            )}
          </div>

          <p className="text-xs leading-[1.4] text-muted-foreground">
            {i18n.t("user.ltf.legal.intro")}
            <a
              href={UserAppRoutes.hubPrivacy()}
              className="underline"
              target="_blank"
              rel="noopener"
              data-testid="ltf-privacy-link"
            >
              {i18n.t("user.ltf.legal.privacy")}
            </a>
            {i18n.t("user.ltf.legal.and")}
            <a
              href={UserAppRoutes.hubTerms()}
              className="underline"
              target="_blank"
              rel="noopener"
              data-testid="ltf-terms-link"
            >
              {i18n.t("user.ltf.legal.terms")}
            </a>
          </p>
        </div>
      </div>

      {/* E2E encryption footer */}
      <div
        className="mt-auto flex w-full max-w-sm gap-2 rounded-2xl bg-card px-3.5 py-4"
        data-testid="ltf-encryption-footer"
      >
        <Lock className="size-4 shrink-0 text-muted-foreground" />
        <p className="text-xs leading-normal text-muted-foreground">
          {i18n.t("user.ltf.encryption")}
        </p>
      </div>
    </div>
  );
}
